import { formatInternational } from 'lib-shared-phone-numbers';
import type { ConversationMessageDeliveryEvent } from 'lib-shared-types-api';

/**
 * Formats a contact point value for display based on its kind.
 *
 * @example
 *   formatContactPointValue({
 *     contact_point_kind: 'EMAIL',
 *     contact_point_value: 'user@example.com',
 *   });
 *   // returns 'user@example.com'
 *
 *   formatContactPointValue({
 *     contact_point_kind: 'PHONE_NUMBER',
 *     contact_point_value: '+12025550100',
 *   });
 *   // returns '+1 202-555-0100'
 *
 * @param event - The delivery event containing contact point information
 * @returns Formatted contact point value
 */
export function formatContactPointValue(
  event: ConversationMessageDeliveryEvent,
): string {
  if (event.contact_point_kind === 'PHONE_NUMBER') {
    // Use the shared phone number formatting utility
    // Falls back to original value if formatting fails
    return (
      formatInternational(event.contact_point_value) ??
      event.contact_point_value
    );
  }

  // Email addresses are returned as-is
  return event.contact_point_value;
}

/**
 * Type guard to check if a delivery event is for a phone number.
 *
 * @param event - The delivery event to check
 * @returns True if the event is for a phone number
 */
export function isPhoneNumberDeliveryEvent(
  event: ConversationMessageDeliveryEvent,
): boolean {
  return event.contact_point_kind === 'PHONE_NUMBER';
}

/**
 * Type guard to check if a delivery event is for an email.
 *
 * @param event - The delivery event to check
 * @returns True if the event is for an email
 */
export function isEmailDeliveryEvent(
  event: ConversationMessageDeliveryEvent,
): boolean {
  return event.contact_point_kind === 'EMAIL';
}
